import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  ArrowRight,
  CheckCircle,
  Wallet,
  CreditCard,
  Clock,
  AlertCircle,
  XCircle,
  ChevronRight,
  MessageSquare,
  Loader2,
  Trash2
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp, PAKISTAN_BANKS } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import {
  whitelistedAccountService,
  pendingRegistrationService
} from '../../services/firebaseService';
import './AccountDashboard.css';

function AccountDashboard() {
  const navigate = useNavigate();
  const {
    currentTransaction,
    setCurrentTransaction,
    setUserType,
    deleteWhitelistedAccount,
    deletePendingRegistration
  } = useApp();
  const { getBrokerPath, brokerId } = useBroker();

  // Broker-specific data state
  const [allWhitelistedAccounts, setAllWhitelistedAccounts] = useState([]);
  const [pendingAccountRegistrations, setPendingAccountRegistrations] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Subscribe to broker-specific whitelisted accounts and pending registrations
  useEffect(() => {
    if (!brokerId) return;

    console.log(`[AccountDashboard] Setting up subscriptions for broker: ${brokerId}`);
    setIsDataLoading(true);

    // Subscribe to broker-specific whitelisted accounts
    const unsubWhitelisted = whitelistedAccountService.subscribeToAll((accounts) => {
      console.log(`[AccountDashboard] Loaded ${accounts.length} whitelisted accounts for ${brokerId}`);
      setAllWhitelistedAccounts(accounts || []);
    }, brokerId);

    // Subscribe to broker-specific pending registrations - get all statuses for user view
    const unsubPending = pendingRegistrationService.subscribeToAll((registrations) => {
      console.log(`[AccountDashboard] Loaded ${registrations.length} pending registrations for ${brokerId}`);
      setPendingAccountRegistrations(registrations || []);
    }, brokerId);

    // Set loading to false after subscriptions are set up
    setTimeout(() => setIsDataLoading(false), 500);

    return () => {
      console.log(`[AccountDashboard] Cleaning up subscriptions for broker: ${brokerId}`);
      if (unsubWhitelisted) unsubWhitelisted();
      if (unsubPending) unsubPending();
    };
  }, [brokerId]);

  // Check if data is ready
  const isDataReady = !isDataLoading;

  const [approvedAccounts, setApprovedAccounts] = useState([]);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [rejectedAccounts, setRejectedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [expandedRemarkId, setExpandedRemarkId] = useState(null);
  const [deletingAccountId, setDeletingAccountId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Redirect if no user data
  useEffect(() => {
    if (!currentTransaction || !currentTransaction.userCnic) {
      navigate(getBrokerPath('/'));
    }
  }, [currentTransaction, navigate, getBrokerPath]);

  // Get accounts for this user (approved, pending, and rejected)
  useEffect(() => {
    if (currentTransaction?.userCnic) {
      const approved = allWhitelistedAccounts.filter(
        acc => acc.cnic === currentTransaction.userCnic && acc.status === 'whitelisted'
      );
      setApprovedAccounts(approved);

      // Get rejected accounts from whitelisted accounts
      const rejectedFromWhitelisted = allWhitelistedAccounts.filter(
        acc => acc.cnic === currentTransaction.userCnic && acc.status === 'rejected'
      );
      setRejectedAccounts(rejectedFromWhitelisted);

      const pending = pendingAccountRegistrations.filter(
        acc => acc.cnic === currentTransaction.userCnic && acc.status === 'pending'
      );
      setPendingAccounts(pending);
    }
  }, [currentTransaction?.userCnic, allWhitelistedAccounts, pendingAccountRegistrations]);

  const getBankLogo = (bankCode) => {
    const bank = PAKISTAN_BANKS.find(b => b.code === bankCode);
    const isWallet = bank?.type === 'wallet';
    const logoUrl = bank?.logo;

    return (
      <div className="bank-logo-wrapper">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={bank?.name || bankCode}
            className="bank-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="bank-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
          {isWallet ? <Wallet size={20} /> : <Building2 size={20} />}
        </div>
      </div>
    );
  };

  const formatIban = (iban) => {
    if (!iban) return '';
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
  };

  const handleProceedWithAccount = () => {
    if (!selectedAccount) return;

    setUserType('returning');
    setCurrentTransaction({
      ...currentTransaction,
      selectedAccount: selectedAccount,
      bankCode: selectedAccount.bankCode,
      bankName: selectedAccount.bankName,
      iban: selectedAccount.iban,
      bankAccountNumber: selectedAccount.accountNumber,
      bankAccountTitle: selectedAccount.accountTitle,
      isReturningUser: true,
    });

    navigate(getBrokerPath('/otp-verification'));
  };

  const handleAddNewAccount = () => {
    setUserType('new');
    setCurrentTransaction({
      ...currentTransaction,
      isReturningUser: false,
    });
    navigate(getBrokerPath('/bank-selection'));
  };

  const toggleRemarks = (accountId, e) => {
    e.stopPropagation();
    setExpandedRemarkId(expandedRemarkId === accountId ? null : accountId);
  };

  const handleDeleteRejectedAccount = async (accountId, e) => {
    e.stopPropagation();

    // If not confirming yet, show confirmation
    if (confirmDeleteId !== accountId) {
      setConfirmDeleteId(accountId);
      return;
    }

    // User confirmed, proceed with deletion
    setDeletingAccountId(accountId);
    setConfirmDeleteId(null);

    try {
      const result = await deleteWhitelistedAccount(accountId, brokerId);
      if (result.success) {
        console.log('Rejected account deleted successfully');
      } else {
        console.error('Failed to delete account:', result.error);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setDeletingAccountId(null);
    }
  };

  const handleDeletePendingAccount = async (accountId, e) => {
    e.stopPropagation();

    // If not confirming yet, show confirmation
    if (confirmDeleteId !== accountId) {
      setConfirmDeleteId(accountId);
      return;
    }

    // User confirmed, proceed with deletion
    setDeletingAccountId(accountId);
    setConfirmDeleteId(null);

    try {
      const result = await deletePendingRegistration(accountId, brokerId);
      if (result.success) {
        console.log('Pending account deleted successfully');
      } else {
        console.error('Failed to delete pending account:', result.error);
      }
    } catch (error) {
      console.error('Error deleting pending account:', error);
    } finally {
      setDeletingAccountId(null);
    }
  };

  const cancelDelete = (e) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  if (!currentTransaction) {
    return null;
  }

  const hasAnyAccounts = approvedAccounts.length > 0 || pendingAccounts.length > 0 || rejectedAccounts.length > 0;

  // Show full page loading spinner until data is actually loaded
  if (!isDataReady) {
    return (
      <div className="dashboard-container animate-fade-in">
        <div className="dashboard-main-card">
          <PageHeader showBackButton={false} greeting="Welcome back" />
          <div className="dashboard-content">
            <div className="section-header">
              <CreditCard size={18} />
              <span>Your Bank Accounts</span>
            </div>
            <div className="accounts-container">
              <div className="loading-accounts">
                <Loader2 size={40} className="spinning" />
                <p>Loading your accounts...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container animate-fade-in">
      {/* Unified Main Card */}
      <div className="dashboard-main-card">
        {/* Header with user info */}
        <PageHeader showBackButton={false} greeting="Welcome back" />

        {/* Content Area */}
        <div className="dashboard-content">
          {/* Section Title */}
          <div className="section-header">
            <CreditCard size={18} />
            <span>Your Bank Accounts</span>
          </div>

          {/* Accounts List - Unified */}
          <div className="accounts-container">
            {/* Approved Accounts */}
            {approvedAccounts.map((account) => (
              <div
                key={account.id}
                className={`account-row ${selectedAccount?.id === account.id ? 'selected' : ''}`}
                onClick={() => handleAccountSelect(account)}
              >
                <div className="account-row-icon approved">
                  {getBankLogo(account.bankCode)}
                </div>
                <div className="account-row-content">
                  <div className="account-row-main">
                    <span className="account-bank-name">{account.bankName}</span>
                    <span className="account-holder">{account.accountTitle}</span>
                  </div>
                  <span className="account-iban">{formatIban(account.iban)}</span>
                </div>
                <div className="account-row-action">
                  <div className="status-badge approved">
                    <CheckCircle size={14} />
                    <span>Active</span>
                  </div>
                  <div className={`select-indicator ${selectedAccount?.id === account.id ? 'active' : ''}`}>
                    {selectedAccount?.id === account.id ? (
                      <CheckCircle size={20} />
                    ) : (
                      <div className="select-circle" />
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Pending Accounts */}
            {pendingAccounts.map((account) => (
              <div key={account.id} className="account-row pending">
                <div className="account-row-icon pending">
                  {getBankLogo(account.bankCode)}
                </div>
                <div className="account-row-content">
                  <div className="account-row-main">
                    <span className="account-bank-name">{account.bankName}</span>
                    <span className="account-holder">{account.accountTitle}</span>
                  </div>
                  <span className="account-iban">{formatIban(account.iban)}</span>
                </div>
                <div className="account-row-action">
                  <div className="status-badge pending">
                    <Clock size={14} />
                    <span>Pending</span>
                  </div>
                  {/* Delete Button for Pending */}
                  {confirmDeleteId === account.id ? (
                    <div className="delete-confirm-actions">
                      <button
                        className="confirm-delete-btn"
                        onClick={(e) => handleDeletePendingAccount(account.id, e)}
                        disabled={deletingAccountId === account.id}
                      >
                        {deletingAccountId === account.id ? (
                          <Loader2 size={14} className="spinning" />
                        ) : (
                          'Yes'
                        )}
                      </button>
                      <button
                        className="cancel-delete-btn"
                        onClick={cancelDelete}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      className="delete-pending-btn"
                      onClick={(e) => handleDeletePendingAccount(account.id, e)}
                      title="Remove this pending account"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Rejected Accounts */}
            {rejectedAccounts.map((account) => (
              <div key={account.id} className="rejected-account-wrapper">
                <div className="account-row rejected">
                  <div className="account-row-icon rejected">
                    {getBankLogo(account.bankCode)}
                  </div>
                  <div className="account-row-content">
                    <div className="account-row-main">
                      <span className="account-bank-name">{account.bankName}</span>
                      <span className="account-holder">{account.accountTitle}</span>
                    </div>
                    <span className="account-iban">{formatIban(account.iban)}</span>
                  </div>
                  <div className="account-row-action">
                    <div className="status-badge rejected">
                      <XCircle size={14} />
                      <span>Rejected</span>
                    </div>
                    {account.rejectionReason && (
                      <button
                        className={`view-remarks-btn ${expandedRemarkId === account.id ? 'active' : ''}`}
                        onClick={(e) => toggleRemarks(account.id, e)}
                      >
                        <MessageSquare size={14} />
                        <span>{expandedRemarkId === account.id ? 'Hide' : 'Reason'}</span>
                      </button>
                    )}
                    {/* Delete Button */}
                    {confirmDeleteId === account.id ? (
                      <div className="delete-confirm-actions">
                        <button
                          className="confirm-delete-btn"
                          onClick={(e) => handleDeleteRejectedAccount(account.id, e)}
                          disabled={deletingAccountId === account.id}
                        >
                          {deletingAccountId === account.id ? (
                            <Loader2 size={14} className="spinning" />
                          ) : (
                            'Yes'
                          )}
                        </button>
                        <button
                          className="cancel-delete-btn"
                          onClick={cancelDelete}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        className="delete-rejected-btn"
                        onClick={(e) => handleDeleteRejectedAccount(account.id, e)}
                        title="Remove this rejected account"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {/* Expandable Remarks Section - Below the row */}
                {expandedRemarkId === account.id && account.rejectionReason && (
                  <div className="inline-remarks-box">
                    <div className="remarks-header">
                      <AlertCircle size={14} />
                      <span>Admin Remarks</span>
                    </div>
                    <p className="remarks-content">{account.rejectionReason}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Empty State - Only show when not loading */}
            {!hasAnyAccounts && (
              <div className="empty-accounts">
                <div className="empty-icon">
                  <CreditCard size={32} />
                </div>
                <h3>No Bank Accounts Yet</h3>
                <p>Add your bank account to start making payments. Please ensure <strong>the account title matches your CNIC name</strong>, otherwise it will be rejected.</p>
              </div>
            )}

            {/* Add New Account Row - Always visible as part of the list */}
            <div className="account-row add-new" onClick={handleAddNewAccount}>
              <div className="account-row-icon add">
                <Plus size={20} />
              </div>
              <div className="account-row-content">
                <div className="account-row-main">
                  <span className="add-title">Add New Bank Account</span>
                  <span className="add-subtitle">Whitelist a new account for payments</span>
                </div>
              </div>
              <div className="account-row-action">
                <ChevronRight size={20} className="chevron-icon" />
              </div>
            </div>
          </div>

          {/* Status Notice */}
          {(pendingAccounts.length > 0 || rejectedAccounts.length > 0) && (
            <div className={`inline-notice ${rejectedAccounts.length > 0 ? 'rejected' : 'pending'}`}>
              <AlertCircle size={16} />
              <span>
                {rejectedAccounts.length > 0
                  ? 'Some accounts were rejected. Add a different account or contact support.'
                  : 'Account verification in progress. You will be notified once approved.'}
              </span>
            </div>
          )}

          {/* Continue Button */}
          {selectedAccount && (
            <div className="action-section">
              <Button
                variant="primary"
                size="large"
                fullWidth
                icon={ArrowRight}
                iconPosition="right"
                onClick={handleProceedWithAccount}
              >
                Continue with {selectedAccount.bankName}
              </Button>
            </div>
          )}
        </div>

        {/* Footer - How it works */}
        <div className="dashboard-footer">
          <div className="process-flow">
            <div className="process-step">
              <div className="process-dot"></div>
              <span>Add Account</span>
            </div>
            <div className="process-line"></div>
            <div className="process-step">
              <div className="process-dot"></div>
              <span>Verify OTP</span>
            </div>
            <div className="process-line"></div>
            <div className="process-step">
              <div className="process-dot"></div>
              <span>Get Approved</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default AccountDashboard;
